import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { Search, Sparkles } from '@dsny/dsny-component-library/icons'
import SearchActions from 'reducers/Search'
import { Tonality } from '@dsny/dsny-component-library'
import { useSearchTracking } from './hooks/useSearchTracking'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import {
  SEARCH_TRACKING_EVENTS,
  SEARCH_ACTIONS,
  SEARCH_PLATFORMS
} from '../../constants/searchTracking'
import {
  Container,
  HeaderRow,
  TitleText,
  ClearButtonDesktop,
  ListItemLink,
  IconWrapper,
  Spacer,
  EmptyState,
  EmptyIconWrapper
} from './RecentlySearched.styles'

import { checkFlag } from '@dsny/dsny-feature-flag-library'
import { useFeatureFlags } from 'contexts/FeatureFlagsContext'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'

export default function RecentlySearched() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMobileMediaQuery()
  const dispatch = useDispatch()
  const recentSearches = useSelector((state) => state.search.recentSearches)
  const { trackSearchEvent } = useSearchTracking()
  const { ready: featureFlagsReady } = useFeatureFlags()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const showAIFilter = featureFlagsReady ? checkFlag('showAIFilter') : false

  const platform = isMobile ? SEARCH_PLATFORMS.MOBILE : SEARCH_PLATFORMS.DESKTOP

  const clearRecentSearches = () => {
    trackSearchEvent?.({
      event: SEARCH_TRACKING_EVENTS.CLEAR_RECENT_SEARCHES,
      action: SEARCH_ACTIONS.CLEAR_RECENT,
      label: '',
      platform,
      source: 'recently_searched',
      type: 'recent_search',
      recent_count: recentSearches.length
    })
    dispatch(SearchActions.clearRecentSearches())
  }

  const searchItems = recentSearches.map((item, index) => (
    <ListItemLink
      key={`${item} ${index + 1}`}
      to={`/search/top?q=${item}`}
      onClick={() =>
        trackSearchEvent?.({
          event: SEARCH_TRACKING_EVENTS.RECENT_SEARCH_CLICK,
          action: SEARCH_ACTIONS.CLICK_RECENT,
          label: item,
          platform,
          source: 'recently_searched',
          type: 'recent_search',
          result_rank: index + 1,
          recent_count: recentSearches.length
        })
      }
    >
      <IconWrapper aria-hidden>
        <Search width={32} height={32} color={theme.colors.neutralW100} />
      </IconWrapper>
      <Tonality.UIText textStyle="body" color={theme.colors.neutralW90}>
        {item}
      </Tonality.UIText>
    </ListItemLink>
  ))

  return (
    <Container $responsiveBreakpointsV2={responsiveBreakpointsV2}>
      {recentSearches.length === 0 ? (
        <EmptyState>
          <EmptyIconWrapper>
            <Sparkles
              width={36}
              height={36}
              style={{ marginTop: 4 }}
              color={theme.colors.player50}
            />
          </EmptyIconWrapper>
          <Tonality.UIHeading
            headingStyle="headline5"
            color={theme.colors.neutralW90}
            style={{ fontSize: 20 }}
          >
            {showAIFilter ? t('search.use-ai-search') : `${t('home.search')}!`}
          </Tonality.UIHeading>
        </EmptyState>
      ) : (
        <>
          <HeaderRow>
            <TitleText
              $responsiveBreakpointsV2={responsiveBreakpointsV2}
              textStyle="bodyBold"
            >
              {t('search.recent-searches')}
            </TitleText>

            {isMobile ? (
              <Tonality.Button
                variant="tertiary"
                setting="player"
                style={{ marginLeft: 'auto' }}
                onClick={clearRecentSearches}
              >
                {t('home.clear-button-text')}
              </Tonality.Button>
            ) : (
              <ClearButtonDesktop
                $responsiveBreakpointsV2={responsiveBreakpointsV2}
                variant="primary"
                setting="player"
                onClick={clearRecentSearches}
              >
                {t('search.clear')}
              </ClearButtonDesktop>
            )}
          </HeaderRow>
          {searchItems}
          <Spacer />
        </>
      )}
    </Container>
  )
}
